import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { TYPE_CONFIG, fmtOz, fmtPieces, plateNowOz, isCheckOnly } from '../lib/plating'
import { tr, trIngredient, trLabel } from '../lib/translations'

// ══════════════════════════════════════════════════════════════════════════════
// PLATE THIS — per-dish plating breakdown
// ══════════════════════════════════════════════════════════════════════════════
//
// Prescribed portion vs. what to actually plate given what was cooked. Shared by
// the Weights tab and the Prep tab so the two can't drift apart.
//
// "Plate now" scales the prescribed portion by r = on hand / need, with the
// uplift capped at +1 oz (see plateNowOz). Scaling down stays linear — being
// short means stretching what you have across every plate. `ea` items keep the
// uncapped piece path they've always had.

export default function PlateThis({
  dishRows,
  ingredients,
  pieceFor,
  mealCounts,
  isSpanish = false,
  translations = null,
  collapsible = false,
  storageKey = null,
  // Pieces-per-plate is editable on the Weights tab (where units are set up) and
  // read-only elsewhere — pass both to make the `ea` cell an input.
  pieceInputs = null,
  onPiece = null,
  // Optionally controlled: the Prep Manager drives this open the moment prep is
  // finished, because at that point plating is the only thing that matters.
  open: openProp,
  onToggle,
  focusMode = false,
  hideHeading = false,
  singleColumn = false,
}) {
  const t = (english) => trLabel(isSpanish, translations, english)

  const [openState, setOpenState] = useState(() => {
    if (!collapsible) return true
    if (!storageKey) return false
    return localStorage.getItem(storageKey) === 'open'
  })
  const [selectedDishId, setSelectedDishId] = useState(null)
  const controlled = openProp !== undefined
  const open = controlled ? openProp : openState

  function toggle() {
    if (controlled) { onToggle?.(!open); return }
    setOpenState((prev) => {
      const next = !prev
      if (storageKey) localStorage.setItem(storageKey, next ? 'open' : 'closed')
      return next
    })
  }

  if (!dishRows.length) return null

  if (focusMode) {
    const selectedRow = dishRows.find(({ dish }) => (dish.dish_id ?? dish.id) === selectedDishId) || dishRows[0]
    const selectedId = selectedRow.dish.dish_id ?? selectedRow.dish.id
    return (
      <div className="h-[calc(100dvh-250px)] min-h-[500px] grid grid-cols-[minmax(260px,30%)_minmax(0,1fr)] rounded-2xl border border-havn-cream-300 bg-white overflow-hidden shadow-soft">
        <aside className="min-h-0 border-r border-havn-cream-300 flex flex-col bg-havn-cream-50/50">
          <div className="p-4 border-b border-havn-cream-300 bg-white"><p className="text-xs uppercase tracking-wider text-havn-gray-400">{dishRows.length} {t('dishes')}</p><h2 className="font-display text-xl text-havn-green-900">{t('Plate quantities')}</h2><p className="text-xs text-havn-gray-500 mt-1">{t('Select one meal to see every Plate Now amount.')}</p></div>
          <div className="min-h-0 overflow-y-auto overscroll-contain">
            {dishRows.map(({ dish, plates }, index) => {
              const dishId = dish.dish_id ?? dish.id
              const active = dishId === selectedId
              return <button key={dishId} onClick={() => setSelectedDishId(dishId)} className={`w-full min-h-[50px] px-3 py-2.5 flex items-center gap-3 text-left border-b border-havn-cream-200 transition-colors ${active ? 'bg-havn-brass-50 border-l-4 border-l-havn-brass-500' : 'bg-white hover:bg-havn-cream-50'}`}><span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${active ? 'bg-havn-green-700 text-white' : 'bg-havn-cream-200 text-havn-gray-600'}`}>{index + 1}</span><span className="min-w-0 flex-1"><strong className="block text-sm text-havn-charcoal truncate">{tr(isSpanish, translations, 'dishes', dishId, 'name', dish.name)}</strong><span className="text-[10px] uppercase tracking-wider text-havn-gray-400">{plates} {t('plates')}</span></span><ChevronRight className="w-4 h-4 text-havn-gray-300" /></button>
            })}
          </div>
        </aside>
        <section className="min-h-0 overflow-y-auto p-4 lg:p-5">
          <PlateThis
            dishRows={[selectedRow]}
            ingredients={ingredients}
            pieceFor={pieceFor}
            mealCounts={mealCounts}
            isSpanish={isSpanish}
            translations={translations}
            pieceInputs={pieceInputs}
            onPiece={onPiece}
            hideHeading
            singleColumn
          />
        </section>
      </div>
    )
  }

  const grid = (
    <div className={`grid grid-cols-1 gap-4 ${singleColumn ? '' : 'lg:grid-cols-2'}`}>
      {dishRows.map(({ dish, plates, comps }) => {
        const dishId = dish.dish_id ?? dish.id
        const dishName = tr(isSpanish, translations, 'dishes', dishId, 'name', dish.name)
        // Which sauce(s) go on this meal. Sauces aren't weighed (no input), but
        // the line cook needs to know what to plate, so show the name(s).
        const sauces = (dish.components || [])
          .filter((c) => c.component_type === 'sauce')
          .filter((c) => c.name)
        // Safety net: any component that is neither weighed (in `comps`) nor a sauce
        // (shown above) must STILL be listed, so a meal component can never be
        // orphaned/hidden — whatever its type. Normally empty (base is weighed now).
        const shownNames = new Set(comps.map((c) => (c.name || '').toLowerCase().trim()))
        const other = (dish.components || [])
          .filter((c) => c.component_type !== 'sauce' && !shownNames.has((c.name || '').toLowerCase().trim()))
          .filter((c) => c.name)
        return (
          <div key={dishId} className="weights-card bg-white rounded-xl border border-havn-cream-300 overflow-hidden break-inside-avoid">
            <div className="px-5 py-3 border-b border-havn-cream-200 bg-havn-cream-50 flex items-center justify-between gap-3">
              <h3 className="font-display text-base font-semibold text-havn-green-900 truncate">{dishName}</h3>
              <div className="flex items-baseline gap-1.5 flex-shrink-0">
                <span className="font-display text-lg font-bold text-havn-green-900 tabular-nums">{plates}</span>
                <span className="text-[10px] uppercase tracking-wider text-havn-gray-500 font-medium">{t('plates')}</span>
              </div>
            </div>
            {mealCounts?.[dish.category] && (
              <div className="px-5 py-2 border-b border-havn-cream-200 bg-white flex items-center gap-2.5 text-[11px]">
                <span className="text-havn-gray-600"><strong className="text-havn-charcoal tabular-nums">{mealCounts[dish.category].glass_standard || 0}</strong> {t('glass')}</span>
                <span className="text-havn-cream-300">·</span>
                <span className="text-havn-gray-600"><strong className="text-havn-charcoal tabular-nums">{mealCounts[dish.category].plastic_standard || 0}</strong> {t('plastic')}</span>
                <span className="text-havn-cream-300">·</span>
                <span className="text-havn-gray-600"><strong className="text-havn-charcoal tabular-nums">{mealCounts[dish.category].total_custom || 0}</strong> {t('customs')}</span>
                <span className="ml-auto text-[10px] uppercase tracking-wider text-havn-gray-400">{t('on the side')}</span>
              </div>
            )}
            {sauces.length > 0 && (
              <div className="px-5 py-2 border-b border-havn-cream-200 bg-white flex items-baseline gap-2 text-[11px]">
                <span className="text-[10px] uppercase tracking-wider text-havn-gray-400 flex-shrink-0">{t('Sauce')}</span>
                <span className="font-medium text-havn-charcoal">{sauces.map((component) => tr(isSpanish, translations, 'recipes', component.recipe_id, 'name', component.name) + ` · ${component.portion_oz || 2} oz`).join(', ')}</span>
              </div>
            )}
            {other.length > 0 && (
              <div className="px-5 py-2 border-b border-havn-cream-200 bg-white flex items-baseline gap-2 text-[11px]">
                <span className="text-[10px] uppercase tracking-wider text-havn-gray-400 flex-shrink-0">{t('Also on the plate')}</span>
                <span className="font-medium text-havn-charcoal">{other.map((component) => trIngredient(isSpanish, translations, component.recipe_id, component.name, component.name)).join(', ')}</span>
              </div>
            )}
            <table className="w-full">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-havn-gray-400 border-b border-havn-cream-200">
                  <th className="px-5 py-2 font-medium">{t('Component')}</th>
                  <th className="px-5 py-2 font-medium text-right">{t('Prescribed')}</th>
                  <th className="px-5 py-2 font-medium text-right">{t('Plate now')}</th>
                </tr>
              </thead>
              <tbody>
                {comps.map((c) => {
                  const ing = ingredients.get(c.key)
                  const isEa = ing?.unit === 'ea'
                  const r = ing?.r
                  const tc = TYPE_CONFIG[c.type] || { dot: 'border-gray-300' }
                  const prescribed = isEa ? fmtPieces(pieceFor(c.id)) : fmtOz(c.portion_oz)
                  // Toppings are never weighed, so there's no measured surplus or
                  // shortfall to redistribute — you plate what the recipe says.
                  // Everything else stays "—" until someone weighs it in.
                  let plateNow = isCheckOnly(c.type) ? prescribed : t('Waiting for cooked weight')
                  if (r != null) {
                    plateNow = isEa
                      ? fmtPieces(pieceFor(c.id) * r)            // pieces: uncapped, unchanged
                      : fmtOz(plateNowOz(c.portion_oz, r))       // oz: uplift capped at +1 oz
                  }
                  const capped = !isEa && r != null && c.portion_oz * r > c.portion_oz + 1
                  return (
                    <tr key={c.id} className="border-b border-havn-cream-100 last:border-b-0">
                      <td className="px-5 py-2.5 align-middle">
                        <div className="flex items-center gap-2">
                          <span className={`inline-block w-2 h-2 rounded-full border ${tc.dot}`} />
                          <span className="text-sm font-medium text-havn-charcoal">{trIngredient(isSpanish, translations, c.recipe_id, c.name, c.name)}</span>
                        </div>
                      </td>
                      <td className="px-5 py-2.5 align-middle text-right text-xs text-havn-gray-500 tabular-nums">
                        {isEa && onPiece ? (
                          <span className="inline-flex items-center gap-1 print-hide">
                            <input
                              type="number" min="0" step="0.5"
                              value={pieceInputs?.[c.id] ?? 1}
                              onChange={(e) => onPiece(c.id, e.target.value)}
                              className="w-12 px-1 py-0.5 text-right tabular-nums bg-white border border-havn-cream-300 rounded focus:outline-none focus:ring-1 focus:ring-havn-green-600"
                            />
                            <span>ea</span>
                          </span>
                        ) : prescribed}
                      </td>
                      <td className="px-5 py-2.5 align-middle text-right">
                        <span className={`font-display text-sm font-semibold tabular-nums ${r != null && r < 1 ? 'text-red-600' : 'text-havn-green-800'}`}>
                          {plateNow}
                        </span>
                        {capped && (
                          <span className="block text-[10px] text-havn-gray-400 leading-tight">{t('capped · extra keeps')}</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )
      })}
    </div>
  )

  if (!collapsible) {
    return (
      <section>
        {!hideHeading && <div className="flex items-center gap-2 mb-3">
          <h2 className="font-display text-xl font-semibold text-havn-green-900">{t('Plate this')}</h2>
          <span className="text-xs text-havn-gray-400">· {dishRows.length} {t('dishes')} · {t('prescribed vs. on-hand')}</span>
        </div>}
        {grid}
      </section>
    )
  }

  // Collapsed by default on the prep tab: cutting and weighing happen hours
  // before plating, so the list stays a clean single column and this is one tap
  // away. Always expanded on paper — the print fallback has to be complete.
  return (
    <section className="space-y-3">
      <button
        onClick={toggle}
        aria-expanded={open}
        className="print-hide w-full min-h-[56px] flex items-center gap-3 px-5 py-3 bg-white border border-havn-cream-300 rounded-xl hover:bg-havn-cream-50 transition-colors"
      >
        <h2 className="font-display text-lg font-semibold text-havn-green-900">{t('Plate this')}</h2>
        <span className="text-xs text-havn-gray-400">· {dishRows.length} {t('dishes')}</span>
        <ChevronDown className={`w-5 h-5 text-havn-gray-400 ml-auto transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <div className={open ? '' : 'hidden print:block'}>{grid}</div>
    </section>
  )
}
